import { useEffect, useRef, useState } from 'react'

const PEEK_HEIGHT = 32 // 每張卡片露出的上緣高度
const SWIPE_THRESHOLD = 40 // 觸控滑動判定門檻（px）

/**
 * 卡片堆疊：多張圖片依序疊放，最上層（目前排序第一個）完整可見並貼齊容器底部，
 * 其餘卡片依序疊在更上方、被上層卡片蓋住下半部，只露出自己的上緣一小截。
 *
 * - 點擊非最上層的卡片：把它換到最上層（重新排序，不開大圖）
 * - 點擊目前已在最上層的卡片：觸發 onCardClick 開啟大圖檢視
 * - 手機可垂直滑動切換最上層卡片（上滑＝換下一張，下滑＝換回上一張）
 *
 * images: [{ id, src, alt }]（陣列第一個為初始最上層／完整可見）
 * 容器高度依目前最上層那張圖的原始比例自動撐開，不強制寫死比例。
 */
export default function StackedImageCards({ images, onCardClick }) {
  const [order, setOrder] = useState(images.map((image) => image.id))
  const [topCardHeight, setTopCardHeight] = useState(null)
  const containerRef = useRef(null)
  const touchStartY = useRef(null)
  const topRatioRef = useRef(null) // 目前最上層圖片的 naturalHeight / naturalWidth

  const imageById = new Map(images.map((image) => [image.id, image]))
  const topId = order[0]

  const bringToTop = (id) => {
    if (id === order[0]) return
    setOrder((prev) => [id, ...prev.filter((x) => x !== id)])
  }

  const handleCardClick = (id) => {
    if (id === topId) {
      onCardClick?.(images.findIndex((image) => image.id === id))
    } else {
      bringToTop(id)
    }
  }

  const recalcHeight = () => {
    const width = containerRef.current?.getBoundingClientRect().width
    if (!width || !topRatioRef.current) return
    setTopCardHeight(topRatioRef.current * width)
  }

  const handleTopImageLoad = (e) => {
    const { naturalWidth, naturalHeight } = e.target
    topRatioRef.current = naturalHeight / naturalWidth
    recalcHeight()
  }

  // 容器寬度改變時（例如拖拉調整視窗寬度）重新依比例算高度，避免圖片實際高度超出舊高度、蓋住下方區塊
  useEffect(() => {
    const el = containerRef.current
    if (!el) return
    const observer = new ResizeObserver(() => recalcHeight())
    observer.observe(el)
    return () => observer.disconnect()
  }, [])

  const shiftNext = () => setOrder((prev) => [...prev.slice(1), prev[0]])
  const shiftPrev = () => setOrder((prev) => [prev[prev.length - 1], ...prev.slice(0, -1)])

  const handleTouchStart = (e) => {
    touchStartY.current = e.touches[0].clientY
  }
  const handleTouchEnd = (e) => {
    if (touchStartY.current == null) return
    const delta = e.changedTouches[0].clientY - touchStartY.current
    if (delta <= -SWIPE_THRESHOLD) shiftNext()
    else if (delta >= SWIPE_THRESHOLD) shiftPrev()
    touchStartY.current = null
  }

  const peekTotal = (images.length - 1) * PEEK_HEIGHT
  const containerHeight = topCardHeight != null ? topCardHeight + peekTotal : undefined

  // order[0]（最上層）貼齊容器底部；排序越後面的卡片疊得越靠上，只露出自己的上緣
  const topFor = (rank) => peekTotal - rank * PEEK_HEIGHT

  return (
    <div
      ref={containerRef}
      className="relative"
      style={{ height: containerHeight, touchAction: 'pan-x', width: '100%', maxWidth: 640, marginLeft: 'auto', marginRight: 'auto' }}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
    >
      {order.map((id, rank) => {
        const image = imageById.get(id)
        const isTop = rank === 0
        return (
          <img
            key={id}
            src={image.src}
            alt={image.alt}
            onLoad={isTop ? handleTopImageLoad : undefined}
            onClick={() => handleCardClick(id)}
            className="absolute inset-x-0 w-full cursor-pointer transition-all duration-300 ease-out"
            style={{
              top: topFor(rank),
              zIndex: images.length - rank,
              boxShadow: isTop ? '0px 8px 24px rgba(110,98,91,0.24)' : '0px 4px 12px rgba(110,98,91,0.16)',
            }}
          />
        )
      })}
    </div>
  )
}
